import { CartUpdatedEvent } from '@theme/events';
import { processGifts } from '@theme/auto-gift-processor';

// Shopify changes quantities, not the variant of an existing line. Add the
// replacement first (stock checked by Shopify), then remove the original.
let changing = false;
const root = () => window.Shopify?.routes?.root || '/';
const properties = (item) => JSON.stringify(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
const plan = (item) => item.selling_plan_allocation?.selling_plan?.id || null;
const matches = (cart, original, variantId) => cart.items.filter((item) =>
  String(item.variant_id) === String(variantId) && properties(item) === properties(original) && plan(item) === plan(original) && !item.parent_relationship);
const quantity = (items) => items.reduce((sum, item) => sum + item.quantity, 0);

async function request(path, body) {
  const response = await fetch(root() + path, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin', cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok || data.errors) throw new Error(data.description || 'La modification du panier a échoué.');
  return data;
}

// Discounts may split or merge otherwise identical lines. Reduce their total,
// using only the freshly returned keys, rather than an obsolete discounted row.
async function reduceGroup(cart, original, variantId, desiredQuantity) {
  const lines = matches(cart, original, variantId);
  let remaining = quantity(lines) - desiredQuantity;
  if (remaining < 0) throw new Error('Le panier a changé. Vérifiez les quantités.');
  if (remaining === 0) return cart;
  const updates = {};
  for (const line of [...lines].reverse()) {
    const removed = Math.min(remaining, line.quantity);
    if (removed) updates[line.key] = line.quantity - removed;
    remaining -= removed;
    if (!remaining) break;
  }
  const updated = await request('cart/update.js', { updates });
  if (quantity(matches(updated, original, variantId)) !== desiredQuantity) {
    throw new Error('Le panier a changé. Vérifiez les quantités.');
  }
  return updated;
}

// Prevent concurrent quantity/removal/checkout actions during the replacement.
function blockDuringChange(event) {
  if (changing && event.target.closest('cart-drawer, cart-component, product-form, cart-icon')) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}
['click', 'change', 'submit'].forEach((type) => document.addEventListener(type, blockDuringChange, true));

class MsrpCartVarnishFinish extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.status = this.querySelector('[role="status"]');
    this.onChange = () => this.replaceVariant();
    this.select.addEventListener('change', this.onChange);
  }

  disconnectedCallback() { this.select.removeEventListener('change', this.onChange); }

  async refreshCart(cart) {
    if (this.closest('cart-component')) {
      await processGifts();
      window.location.reload();
      return;
    }
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) { window.location.reload(); return; }
    const sectionIds = [drawer.dataset.sectionId, 'auto-gifts'];
    // A section read cannot accidentally change quantities if its request fails.
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionIds.join(','));
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    const sections = await response.json();
    if (!response.ok || !sections[drawer.dataset.sectionId]) throw new Error('Veuillez actualiser le panier pour afficher sa mise à jour.');
    await new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (!this.isConnected) { observer.disconnect(); clearTimeout(timer); resolve(); }
      });
      observer.observe(drawer, { childList: true, subtree: true });
      const timer = setTimeout(() => { observer.disconnect(); resolve(); }, 8000);
      document.dispatchEvent(new CartUpdatedEvent(cart, {
        itemCount: cart.item_count, source: 'msrp-cart-varnish-finish', sections,
      }));
    });
  }

  async replaceVariant() {
    const targetId = this.select.value;
    if (changing || targetId === this.dataset.variantId) return;
    changing = true;
    const controls = [...document.querySelectorAll('cart-drawer button, cart-drawer input, cart-drawer select, cart-component button, cart-component input, cart-component select')]
      .filter((control) => !control.disabled);
    controls.forEach((control) => { control.disabled = true; });
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = 'Mise à jour…';
    let original, initialTargetQuantity, initialSourceQuantity, added = false, completed = false;
    try {
      const before = await request('cart.js');
      original = before.items.find((item) => item.key === this.dataset.lineKey);
      if (!original) throw new Error('Le panier a changé. Actualisez-le avant de modifier la finition.');
      initialTargetQuantity = quantity(matches(before, original, targetId));
      initialSourceQuantity = quantity(matches(before, original, original.variant_id));
      const replacement = { id: Number(targetId), quantity: original.quantity, properties: original.properties || {} };
      if (plan(original)) replacement.selling_plan = plan(original);
      await request('cart/add.js', { items: [replacement] });
      added = true;
      const afterAdd = await request('cart.js');
      if (quantity(matches(afterAdd, original, original.variant_id)) !== initialSourceQuantity || quantity(matches(afterAdd, original, targetId)) !== initialTargetQuantity + original.quantity) {
        throw new Error('Le panier a changé pendant la modification. Vérifiez les quantités.');
      }
      const cart = await reduceGroup(afterAdd, original, original.variant_id, initialSourceQuantity - original.quantity);
      if (quantity(matches(cart, original, targetId)) !== initialTargetQuantity + original.quantity) {
        throw new Error('Le panier a changé pendant la modification. Vérifiez les quantités.');
      }
      completed = true;
      await this.refreshCart(cart);
    } catch (error) {
      let message = error.message || 'La finition n’a pas pu être modifiée.';
      try {
        let cart = await request('cart.js');
        if (original && !completed) {
          const oldLines = matches(cart, original, original.variant_id);
          const targetLines = matches(cart, original, targetId);
          const targetQuantity = quantity(targetLines);
          if (quantity(oldLines) === initialSourceQuantity - original.quantity && targetQuantity === initialTargetQuantity + original.quantity) {
            // A lost update.js response can still mean a successful replacement.
            completed = true;
          } else if (quantity(oldLines) === initialSourceQuantity && targetQuantity > initialTargetQuantity && targetQuantity <= initialTargetQuantity + original.quantity) {
            // Also undo a partial add caused by Shopify's stock limit response.
            cart = await reduceGroup(cart, original, targetId, initialTargetQuantity);
            message = 'Finition inchangée. ' + message;
          } else if (added) {
            message = 'Vérifiez les finitions et les quantités du panier avant de continuer.';
          }
        }
        if (!completed) document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message } }));
        await this.refreshCart(cart);
      } catch {
        this.status.textContent = 'Connexion interrompue. Actualisez le panier pour vérifier la finition et les quantités.';
      }
      if (this.isConnected) {
        this.select.value = this.dataset.variantId;
        this.status.textContent = completed ? 'Panier mis à jour. Actualisez la page.' : message;
      }
    } finally {
      controls.forEach((control) => { control.disabled = false; });
      this.removeAttribute('aria-busy');
      changing = false;
    }
  }
}

if (!customElements.get('msrp-cart-varnish-finish')) customElements.define('msrp-cart-varnish-finish', MsrpCartVarnishFinish);
